import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/portal/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Loader2, Zap } from "lucide-react";

export default function Login() {
  const { user, login, errMessage } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/portal", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/portal", { replace: true });
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden overflow-hidden bg-navy lg:flex lg:flex-col lg:justify-between lg:p-12">
        <div className="grid-texture pointer-events-none absolute inset-0 opacity-40" />
        <div className="pointer-events-none absolute -right-32 top-1/4 h-[380px] w-[380px] rounded-full bg-electric/20 blur-[130px]" />
        <Link to="/" className="relative z-10">
          <img src="/ieg-lockup-dark.png?v=4" alt="Innervation Energy Group" className="h-14 w-auto" />
        </Link>
        <div className="relative z-10 max-w-md">
          <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-energy">
            <span className="h-px w-8 bg-energy" /> Partner Portal
          </p>
          <h1 className="mt-6 font-heading text-4xl font-semibold leading-tight text-white">
            Manage your energy pipeline in one place.
          </h1>
          <p className="mt-4 text-lg leading-relaxed text-white/70">
            Submit potential customers, track every application from LOA to executed contract,
            and grow your team.
          </p>
        </div>
        <p className="relative z-10 font-mono text-[11px] uppercase tracking-[0.18em] text-white/40">
          Electricity · Natural Gas · Virtual Power Plant Network
        </p>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center bg-ice px-6 py-16">
        <div className="w-full max-w-sm">
          <Link to="/" className="mb-8 inline-flex items-center gap-2 lg:hidden">
            <img src="/ieg-lockup-light.png?v=4" alt="Innervation Energy Group" className="h-11 w-auto" />
          </Link>
          <div className="mb-8">
            <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-md bg-navy text-energy">
              <Zap className="h-5 w-5" />
            </div>
            <h2 className="font-heading text-2xl font-semibold text-navy">Sign in</h2>
            <p className="mt-1 text-sm text-slatey">Access the Innervation Energy Group portal.</p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-navy">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="mt-1.5 bg-white"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-navy">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="mt-1.5 bg-white"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">
                {error}
              </p>
            )}

            <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full bg-electric text-white hover:bg-electric-hover">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Sign in <ArrowRight className="ml-1 h-4 w-4" /></>)}
            </Button>
          </form>

          <p className="mt-8 text-center text-xs text-slatey">
            Reps and customers are added by invitation or a rep's application link.
          </p>
        </div>
      </div>
    </div>
  );
}
